import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { academiesData } from '@/data/academyData';

interface ClientInfo {
  id: string;
  plan: string;
  subscription_start: string;
}

interface CompletedModule {
  academy_id: string;
  module_id: number;
}

export const useModuleAccess = () => {
  const { user } = useAuth();
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [completedModules, setCompletedModules] = useState<CompletedModule[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchClientInfo = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Get client info
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('id, plan, subscription_start')
        .eq('user_id', user.id)
        .maybeSingle();

      if (clientError || !client) {
        setIsLoading(false);
        return;
      }

      setClientInfo(client);

      // Get completed modules for monthly plan
      if (client.plan !== 'Lifetime') {
        const { data: completions } = await supabase
          .from('client_module_completions')
          .select('academy_id, module_id')
          .eq('client_id', client.id);

        if (completions) {
          setCompletedModules(completions as CompletedModule[]);
        }
      }

      setIsLoading(false);
    };

    fetchClientInfo();
  }, [user]);

  // Calculate unlocked module count
  const unlockedModuleCount = useMemo(() => {
    if (!clientInfo) return 0;

    // Lifetime plan = all modules unlocked
    if (clientInfo.plan === 'Lifetime') {
      return 50; // Total modules across all academies
    }

    // Monthly plan: 5 base + 5 per completed module
    const baseModules = 5;
    const bonusModules = completedModules.length * 5;
    const totalUnlocked = baseModules + bonusModules;

    // Cap at 50 total modules
    return Math.min(totalUnlocked, 50);
  }, [clientInfo, completedModules]);

  // Build list of unlocked modules
  const unlockedModules = useMemo(() => {
    const unlocked: { academyId: string; moduleId: number }[] = [];
    let count = 0;

    // Iterate through academies and modules in order
    for (const academy of academiesData) {
      for (const module of academy.modules) {
        if (count < unlockedModuleCount) {
          unlocked.push({ academyId: academy.id, moduleId: module.id });
          count++;
        } else {
          break;
        }
      }
      if (count >= unlockedModuleCount) break;
    }

    return unlocked;
  }, [unlockedModuleCount]);

  const isModuleUnlocked = (academyId: string, moduleId: number): boolean => {
    if (!clientInfo) return false;
    
    // Lifetime = all unlocked
    if (clientInfo.plan === 'Lifetime') return true;

    return unlockedModules.some(
      m => m.academyId === academyId && m.moduleId === moduleId
    );
  };

  const isAcademyFullyUnlocked = (academyId: string): boolean => {
    if (!clientInfo) return false;
    if (clientInfo.plan === 'Lifetime') return true;

    const academy = academiesData.find(a => a.id === academyId);
    if (!academy) return false;

    return academy.modules.every(m => isModuleUnlocked(academyId, m.id));
  };

  const getUnlockedModulesInAcademy = (academyId: string): number => {
    if (!clientInfo) return 0;
    if (clientInfo.plan === 'Lifetime') {
      const academy = academiesData.find(a => a.id === academyId);
      return academy?.modules.length || 0;
    }

    return unlockedModules.filter(m => m.academyId === academyId).length;
  };

  return {
    isLoading,
    clientInfo,
    unlockedModuleCount,
    totalModules: 50,
    isModuleUnlocked,
    isAcademyFullyUnlocked,
    getUnlockedModulesInAcademy,
    completedModulesCount: completedModules.length,
    isLifetime: clientInfo?.plan === 'Lifetime',
  };
};
